'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function QuadraticFormulaCalculator() {
  const t = useTranslations("tools.quadraticFormulaCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    a: '1',
    b: '1',
    c: '0.25'
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseFraction = (input: string): number => {
    input = input.trim();
    if (input.includes('/')) {
      const parts = input.split('/');
      if (parts.length === 2) {
        const numerator = parseFloat(parts[0]);
        const denominator = parseFloat(parts[1]);
        if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
          return numerator / denominator;
        }
      }
      return NaN;
    }
    return parseFloat(input);
  };

  const formatNumber = (num: number, decimals: number = 6): string => {
    if (Math.abs(num - Math.round(num)) < 0.0000001) {
      return Math.round(num).toString();
    }
    const rounded = Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
    return rounded.toString();
  };

  const formatComplex = (real: number, imag: number, decimals: number = 6): string => {
    const realStr = formatNumber(real, decimals);
    const imagStr = formatNumber(Math.abs(imag), decimals);

    if (imag >= 0) {
      return `${realStr} + ${imagStr}i`;
    } else {
      return `${realStr} - ${imagStr}i`;
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const a = parseFraction(inputs.a);
    const b = parseFraction(inputs.b);
    const c = parseFraction(inputs.c);

    if (isNaN(a) || isNaN(b) || isNaN(c)) {
      setError(t('errorInvalidInput'));
      return;
    }

    if (a === 0) {
      setError(t('errorZeroA'));
      return;
    }

    const discriminant = b * b - 4 * a * c;
    let interpretation = '';
    let solutions: any[] = [];

    if (discriminant > 0) {
      interpretation = t('interpretationTwoReal');
      const sqrtDiscriminant = Math.sqrt(discriminant);
      const x1 = (-b + sqrtDiscriminant) / (2 * a);
      const x2 = (-b - sqrtDiscriminant) / (2 * a);

      solutions = [
        { label: 'x₁', value: formatNumber(x1), isComplex: false },
        { label: 'x₂', value: formatNumber(x2), isComplex: false }
      ];
    } else if (discriminant === 0) {
      interpretation = t('interpretationOneReal');
      const x = -b / (2 * a);

      solutions = [
        { label: 'x', value: formatNumber(x), isComplex: false }
      ];
    } else {
      interpretation = t('interpretationTwoComplex');
      const realPart = -b / (2 * a);
      const imagPart = Math.sqrt(-discriminant) / (2 * a);

      solutions = [
        { label: 'x₁', value: formatComplex(realPart, imagPart), isComplex: true },
        { label: 'x₂', value: formatComplex(realPart, -imagPart), isComplex: true }
      ];
    }

    setResults({
      discriminant: formatNumber(discriminant),
      interpretation,
      solutions
    });
  };

  const handleClear = () => {
    setInputs({ a: '1', b: '1', c: '0.25' });
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const toolH2Style = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const equationDisplayStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const equationStyle = getResponsiveStyle(
    { color: '#2563eb', fontWeight: 600, fontFamily: 'Georgia, serif' },
    { fontSize: '1.3rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const coefficientsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.25rem', marginBottom: '1.25rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' },
    { gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const coefficientSpanStyle = getResponsiveStyle(
    { color: '#2563eb', fontFamily: 'Georgia, serif' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      fontFamily: 'monospace',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.8rem', fontSize: '1.05rem' },
    { padding: '0.875rem', fontSize: '1.1rem' }
  );

  const helperTextStyle = getResponsiveStyle(
    { textAlign: 'center' as const, color: '#64748b', marginBottom: '1.25rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
      fontWeight: 600
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.95rem 2rem', fontSize: '1.05rem', width: 'auto' },
    { padding: '1rem 2.5rem', fontSize: '1.1rem', width: 'auto' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      background: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontWeight: 600
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.95rem 2rem', fontSize: '1.05rem', width: 'auto' },
    { padding: '1rem 2.5rem', fontSize: '1.1rem', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      animation: 'slideIn 0.4s ease'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: colors.textPrimary, textAlign: 'center' as const, marginBottom: '1.25rem', fontWeight: 700 },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const discriminantInfoStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.25rem',
      borderLeft: '4px solid #2563eb'
    },
    { padding: '1rem' },
    { padding: '1.15rem' },
    { padding: '1.25rem' }
  );

  const discriminantLabelStyle = {
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem'
  };

  const discriminantValueStyle = getResponsiveStyle(
    { color: '#2563eb', fontWeight: 700, fontFamily: 'monospace' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const discriminantInterpretationStyle = getResponsiveStyle(
    { marginTop: '0.75rem', color: '#64748b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const solutionsContainerStyle = {
    display: 'grid',
    gap: '1rem'
  };

  const solutionItemStyle = (isComplex: boolean) => getResponsiveStyle(
    {
      background: isComplex
        ? 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)'
        : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      borderLeft: `4px solid ${isComplex ? '#ec4899' : '#f59e0b'}`
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const solutionLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const solutionValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700, fontFamily: 'monospace' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const formulaDisplayStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.75rem',
      marginTop: '1.25rem',
      textAlign: 'center' as const
    },
    { padding: '1rem' },
    { padding: '1.15rem' },
    { padding: '1.25rem' }
  );

  const formulaLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#64748b', marginBottom: '0.75rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const formulaStyle = getResponsiveStyle(
    { color: '#2563eb', fontFamily: 'Georgia, serif' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fee2e2',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: '4px solid #ef4444',
      fontWeight: 500
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '0.95rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolH2Style}>🧮 {t('toolTitle')}</h2>
          </div>

          <div style={equationDisplayStyle}>
            <div style={equationStyle}>
              ax<sup>2</sup> + bx + c = 0
            </div>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={coefficientsGridStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="coeff-a" style={labelStyle}>
                  {t('coefficientA')} <span style={coefficientSpanStyle}>a</span>
                </label>
                <input
                  type="text"
                  id="coeff-a"
                  value={inputs.a}
                  onChange={(e) => setInputs({...inputs, a: e.target.value})}
                  style={inputStyle}
                  placeholder={t('placeholderA')}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="coeff-b" style={labelStyle}>
                  {t('coefficientB')} <span style={coefficientSpanStyle}>b</span>
                </label>
                <input
                  type="text"
                  id="coeff-b"
                  value={inputs.b}
                  onChange={(e) => setInputs({...inputs, b: e.target.value})}
                  style={inputStyle}
                  placeholder={t('placeholderB')}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="coeff-c" style={labelStyle}>
                  {t('coefficientC')} <span style={coefficientSpanStyle}>c</span>
                </label>
                <input
                  type="text"
                  id="coeff-c"
                  value={inputs.c}
                  onChange={(e) => setInputs({...inputs, c: e.target.value})}
                  style={inputStyle}
                  placeholder={t('placeholderC')}
                  required
                />
              </div>
            </div>

            <div style={helperTextStyle}>
              {t('helperText')}
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>
                {t('clearButton')}
              </button>
            </div>
          </form>

          {error && (
            <div style={errorMessageStyle}>
              {error}
            </div>
          )}

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={discriminantInfoStyle}>
                <div style={discriminantLabelStyle}>{t('discriminantLabel')}</div>
                <div style={discriminantValueStyle}>{results.discriminant}</div>
                <div style={discriminantInterpretationStyle}>{results.interpretation}</div>
              </div>

              <div style={solutionsContainerStyle}>
                {results.solutions.map((solution: any, index: number) => (
                  <div key={index} style={solutionItemStyle(solution.isComplex)}>
                    <div style={solutionLabelStyle}>{t('solutionLabel')} {solution.label}:</div>
                    <div style={solutionValueStyle}>{solution.value}</div>
                  </div>
                ))}
              </div>

              <div style={formulaDisplayStyle}>
                <div style={formulaLabelStyle}>{t('formulaUsedLabel')}</div>
                <div style={formulaStyle}>x = (-b ± √(b² - 4ac)) / 2a</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Introduction */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Desc')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightUniversalTitle')}</h3>
        <p style={paragraphStyle}>{t('insightUniversalDesc')}</p>

        <h3 style={h3Style}>{t('insightDiscriminantTitle')}</h3>
        <p style={paragraphStyle}>{t('insightDiscriminantDesc')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>Δ {'>'} 0:</strong> {t('discriminantPositive')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>Δ = 0:</strong> {t('discriminantZero')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>Δ {'<'} 0:</strong> {t('discriminantNegative')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightApplicationsTitle')}</h3>
        <p style={paragraphStyle}>{t('insightApplicationsDesc')}</p>

        <h3 style={h3Style}>{t('insightMethodsTitle')}</h3>
        <p style={paragraphStyle}>{t('insightMethodsDesc')}</p>
      </section>

      {/* Understanding the Formula */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('understandingTitle')}</h2>

        <h3 style={h3Style}>{t('standardFormTitle')}</h3>
        <p style={paragraphStyle}>{t('standardFormDesc')}</p>

        <h3 style={h3Style}>{t('derivationTitle')}</h3>
        <p style={paragraphStyle}>{t('derivationDesc')}</p>

        <h3 style={h3Style}>{t('componentsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>-b:</strong> {t('componentNegB')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>±:</strong> {t('componentPlusMinus')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>√(b² - 4ac):</strong> {t('componentSqrt')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>2a:</strong> {t('component2a')}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>

        <h3 style={h3Style}>{t('practiceStandardFormTitle')}</h3>
        <p style={paragraphStyle}>{t('practiceStandardFormDesc')}</p>

        <h3 style={h3Style}>{t('practiceIdentifyTitle')}</h3>
        <p style={paragraphStyle}>{t('practiceIdentifyDesc')}</p>

        <h3 style={h3Style}>{t('practiceCheckTitle')}</h3>
        <p style={paragraphStyle}>{t('practiceCheckDesc')}</p>

        <h3 style={h3Style}>{t('practiceSimplifyTitle')}</h3>
        <p style={paragraphStyle}>{t('practiceSimplifyDesc')}</p>

        <h3 style={h3Style}>{t('practiceVerifyTitle')}</h3>
        <p style={paragraphStyle}>{t('practiceVerifyDesc')}</p>
      </section>

      {/* Common Mistakes */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('mistakesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('mistake1Title')}</strong> {t('mistake1Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('mistake2Title')}</strong> {t('mistake2Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('mistake3Title')}</strong> {t('mistake3Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('mistake4Title')}</strong> {t('mistake4Desc')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('mistake5Title')}</strong> {t('mistake5Desc')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={h3Style}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.calculatorsoup.com/calculators/algebra/quadratic-formula-calculator.php"
               style={linkStyle}
               target="_blank"
               rel="noopener noreferrer">
              {t('ref1')}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://statisticsbyjim.com/basics/quadratic-formula/"
               style={linkStyle}
               target="_blank"
               rel="noopener noreferrer">
              {t('ref2')}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://byjus.com/maths/quadratic-formula/"
               style={linkStyle}
               target="_blank"
               rel="noopener noreferrer">
              {t('ref3')}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://photomath.com/en/articles/quadratic-equations-formula-use-examples-and-solutions/"
               style={linkStyle}
               target="_blank"
               rel="noopener noreferrer">
              {t('ref4')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}